import React, { useState } from 'react';
import { Info, FileText, ShieldCheck, Users, EnvelopeSimple, Trash, Scales } from '@phosphor-icons/react';

const tabs = [
  { id: 'about', label: 'About', icon: Info },
  { id: 'terms', label: 'Terms & Conditions', icon: FileText },
  { id: 'privacy', label: 'Privacy Policy', icon: ShieldCheck },
  { id: 'copyright', label: 'Copyright Notice', icon: Scales },
  { id: 'community', label: 'Community Guidelines', icon: Users },
  { id: 'contact', label: 'Contact Information', icon: EnvelopeSimple },
  { id: 'delete', label: 'Delete Account', icon: Trash },
];

const SectionTitle = ({ children }) => (
  <h2 className="font-heading font-bold text-xl uppercase text-white mt-8 mb-4">{children}</h2>
);

const SubTitle = ({ children }) => (
  <h3 className="font-bold text-white mt-6 mb-2">{children}</h3>
);

const P = ({ children }) => (
  <p className="text-[#8892B0] mb-3 leading-relaxed">{children}</p>
);

const BulletList = ({ items }) => (
  <ul className="space-y-1.5 mb-4 ml-4">
    {items.map((item) => (
      <li key={item} className="text-[#8892B0] flex items-start gap-2">
        <span className="text-[#007AFF] mt-1.5 text-xs">&#9679;</span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const AboutTab = () => (
  <div>
    <P>Trader Reset AI is your personal trading psychology coach designed to help you stay disciplined and make better decisions—every single day.</P>

    <SectionTitle>Stop Emotional Trading. Build Discipline. Trade With Confidence.</SectionTitle>
    <P>Most traders don't lose because of strategy.</P>
    <P>They lose because of emotion.</P>
    <BulletList items={['FOMO', 'Revenge trading', 'Overtrading', 'Breaking rules']} />

    <SectionTitle>What You Can Do</SectionTitle>

    <SubTitle>AI Trade Coach</SubTitle>
    <BulletList items={[
      'Get instant feedback before or after trades',
      'Identify emotional mistakes',
      'Improve your decision-making',
    ]} />

    <SubTitle>Trading Journal</SubTitle>
    <BulletList items={[
      'Track your trades and emotions',
      'Spot patterns in your behavior',
      'Build consistency over time',
    ]} />

    <SubTitle>Screenshot Analysis</SubTitle>
    <BulletList items={[
      'Upload your charts',
      'Get AI insights on your decisions',
      'Understand what went wrong (and right)',
    ]} />

    <SubTitle>Emotional Reset Tool</SubTitle>
    <BulletList items={[
      'Feeling frustrated or impulsive?',
      'Use the built-in reset protocol to regain control',
    ]} />

    <SubTitle>Daily Discipline System</SubTitle>
    <BulletList items={[
      'Pre-trade checklists',
      'Routine reminders',
      'Weekly performance reviews',
    ]} />

    <SubTitle>Community (Optional)</SubTitle>
    <BulletList items={[
      'Share your journey',
      'Learn from other traders',
      'Stay accountable',
    ]} />

    <SectionTitle>Why This Website Is Different</SectionTitle>
    <P>This is NOT another signal Website.</P>
    <P>This is NOT a "get rich quick" tool.</P>
    <P>This is a discipline system built for real traders who want long-term consistency.</P>

    <SectionTitle>Who It's For</SectionTitle>
    <BulletList items={[
      'Crypto traders',
      'Beginners and experienced traders',
      'If you struggle with emotions while trading, this website is for you.',
    ]} />

    <SectionTitle>Subscription</SectionTitle>
    <P>Some features require a subscription:</P>
    <BulletList items={[
      'Unlimited AI coaching',
      'Advanced analytics',
      'Premium tools',
    ]} />

    <div className="mt-8 p-6 bg-[#007AFF]/10 border border-[#007AFF]/20">
      <p className="text-white font-bold text-lg">Your strategy doesn't need fixing.</p>
      <p className="text-[#007AFF] font-bold text-lg">Your discipline does.</p>
    </div>

    <div className="mt-8 pt-6 border-t border-white/10 text-center">
      <p className="text-[#5C6B82] text-sm">&copy; 2026 Trader Reset AI. All rights reserved.</p>
    </div>
  </div>
);

const TermsTab = () => (
  <div>
    <P>You agree to use this app and ebook for lawful purposes only.</P>
    <P>You must not:</P>
    <BulletList items={[
      'Use the website for illegal or harmful activity',
      'Attempt to hack, reverse engineer, or disrupt the service',
      'Misuse community features',
    ]} />
    <P>We reserve the right to suspend or terminate accounts that violate these rules.</P>

    <SectionTitle>Educational Purpose Only</SectionTitle>
    <P>All content provided is for educational purposes only.</P>
    <P>We do not provide:</P>
    <BulletList items={[
      'Financial advice',
      'Investment recommendations',
      'Trading signals',
    ]} />
    <P>All decisions are your responsibility.</P>

    <SectionTitle>Account Responsibility</SectionTitle>
    <P>You are responsible for:</P>
    <BulletList items={[
      'Maintaining the confidentiality of your account',
      'All activity under your account',
    ]} />
    <P>We are not liable for unauthorized access.</P>

    <SectionTitle>Payments & Refunds</SectionTitle>
    <P>All purchases (ebooks, subscriptions, digital products) are:</P>
    <BulletList items={[
      'Final unless stated otherwise',
      'Non-refundable due to the digital nature of the product',
    ]} />
    <P>Subscription billing will renew automatically unless cancelled.</P>

    <SectionTitle>Intellectual Property</SectionTitle>
    <P>All content (ebook, website features, templates, branding) is owned by us.</P>
    <P>You may not:</P>
    <BulletList items={[
      'Copy, resell, or redistribute content',
      'Share paid materials publicly',
    ]} />
    <P>Violation may result in legal action.</P>

    <SectionTitle>AI-Generated Content</SectionTitle>
    <P>The website includes AI-generated responses.</P>
    <P>You acknowledge that:</P>
    <BulletList items={[
      'Outputs may not always be accurate',
      'They are for guidance only',
      'They are not financial advice',
    ]} />

    <SectionTitle>Limitation of Liability</SectionTitle>
    <P>We are not liable for:</P>
    <BulletList items={[
      'Trading losses',
      'Financial decisions',
      'Indirect damages',
    ]} />
    <P>Use of this product is at your own risk.</P>

    <SectionTitle>Coaching Services</SectionTitle>
    <P>We may offer optional 1:1 coaching services as part of premium plans or separate purchases.</P>
    <P>By purchasing coaching, you acknowledge:</P>
    <BulletList items={[
      'Coaching is for educational and informational purposes only',
      'Coaching does not constitute financial or investment advice',
      'Results are not guaranteed',
      'You remain fully responsible for your trading decisions',
    ]} />
    <P>We reserve the right to modify or discontinue coaching services at any time.</P>

    <SubTitle>Coaching Disclaimer</SubTitle>
    <P>Any 1:1 coaching provided through this platform is intended for:</P>
    <BulletList items={[
      'Trading psychology improvement',
      'Discipline development',
      'Educational support',
    ]} />
    <P>Coaching does not include:</P>
    <BulletList items={[
      'Financial advice',
      'Trade recommendations',
      'Signals or guaranteed strategies',
    ]} />
    <P>All decisions remain your responsibility.</P>

    <SectionTitle>Modifications</SectionTitle>
    <P>We may update these terms at any time. Continued use means you accept the updated terms.</P>

    <SectionTitle>Age Restriction</SectionTitle>
    <P>This website is not intended for users under 18.</P>
  </div>
);

const PrivacyTab = () => (
  <div>
    <P>Your privacy is important to us. This policy explains how we collect and use your data.</P>

    <SectionTitle>Information We Collect</SectionTitle>
    <P>We may collect:</P>

    <SubTitle>Personal Information</SubTitle>
    <BulletList items={['Name', 'Email address', 'Account login details']} />

    <SubTitle>Usage Data</SubTitle>
    <BulletList items={['Website activity', 'Features used', 'Device information']} />

    <SubTitle>User Content</SubTitle>
    <BulletList items={[
      'Uploaded images (charts, screenshots)',
      'Journal entries',
      'Posts and comments',
    ]} />

    <SectionTitle>How We Use Your Information</SectionTitle>
    <P>We use your data to:</P>
    <BulletList items={[
      'Provide and improve the website',
      'Personalize AI feedback',
      'Send notifications',
      'Improve user experience',
    ]} />

    <SectionTitle>AI Processing</SectionTitle>
    <P>Content you submit (text, images) may be processed by AI systems to provide feedback.</P>
    <P>We do not guarantee accuracy of AI outputs.</P>

    <SectionTitle>Data Sharing</SectionTitle>
    <P>We do not sell your personal data.</P>
    <P>We may share data with:</P>
    <BulletList items={[
      'Service providers (hosting, analytics)',
      'Legal authorities if required',
    ]} />

    <SectionTitle>Data Storage</SectionTitle>
    <P>Your data is stored securely using industry-standard practices.</P>
    <P>However, no system is 100% secure.</P>

    <SectionTitle>Your Rights</SectionTitle>
    <P>You may:</P>
    <BulletList items={[
      'Request access to your data',
      'Request deletion of your account',
      'Opt out of emails/notifications',
    ]} />

    <SectionTitle>Children's Privacy</SectionTitle>
    <P>This website is not intended for users under 18.</P>

    <SectionTitle>Changes to This Policy</SectionTitle>
    <P>We may update this policy. Continued use means acceptance.</P>

    <SectionTitle>Coaching Services</SectionTitle>
    <P>We may offer optional 1:1 coaching services as part of premium plans or separate purchases.</P>
    <P>By purchasing coaching, you acknowledge:</P>
    <BulletList items={[
      'Coaching is for educational and informational purposes only',
      'Coaching does not constitute financial or investment advice',
      'Results are not guaranteed',
      'You remain fully responsible for your trading decisions',
    ]} />
    <P>We reserve the right to modify or discontinue coaching services at any time.</P>

    <SubTitle>Coaching Disclaimer</SubTitle>
    <P>Any 1:1 coaching provided through this platform is intended for:</P>
    <BulletList items={[
      'Trading psychology improvement',
      'Discipline development',
      'Educational support',
    ]} />
    <P>Coaching does not include:</P>
    <BulletList items={[
      'Financial advice',
      'Trade recommendations',
      'Signals or guaranteed strategies',
    ]} />
    <P>All decisions remain your responsibility.</P>

    <SubTitle>Coaching Data</SubTitle>
    <P>If you participate in 1:1 coaching, we may collect:</P>
    <BulletList items={[
      'Messages and communication history',
      'Trade examples you share',
      'Notes related to your progress',
    ]} />
    <P>This data is used only to:</P>
    <BulletList items={[
      'Provide personalized coaching',
      'Improve your experience',
    ]} />
    <P>We do not sell or share this data.</P>
  </div>
);

const CopyrightTab = () => (
  <div>
    <SectionTitle>Copyright Notice (Ebook + Website)</SectionTitle>
    <P>&copy; 2026 ETCY. All Rights Reserved.</P>
    <P>The Trader's Psychology Reset Guide and Trader Reset AI (including all content, text, graphics, frameworks, templates, and materials) are the intellectual property of ETCY.</P>

    <SubTitle>Use of Content</SubTitle>
    <P>No part of this publication or application may be:</P>
    <BulletList items={[
      'Reproduced',
      'Distributed',
      'Transmitted',
      'Stored in a retrieval system',
    ]} />
    <P>in any form or by any means (electronic, mechanical, photocopying, recording, or otherwise) without prior written permission from the copyright owner.</P>
  </div>
);

const CommunityTab = () => (
  <div>
    <P>Users must not:</P>
    <BulletList items={[
      'Share harmful, abusive, or misleading content',
      'Promote scams or financial manipulation',
      'Harass other users',
      'Promote scams or misleading crypto projects',
      'Share harmful or abusive content',
    ]} />
    <P>We reserve the right to remove content or users.</P>
  </div>
);

const ContactTab = () => (
  <div>
    <SectionTitle>Get In Touch</SectionTitle>
    <P>For any questions, support requests, or business inquiries, reach out to us at:</P>
    <div className="mt-4 p-6 bg-[#007AFF]/10 border border-[#007AFF]/20">
      <a
        href="mailto:traderresetai@protonmail.com"
        className="text-[#007AFF] font-bold text-lg hover:underline"
        data-testid="contact-email"
      >
        traderresetai@protonmail.com
      </a>
    </div>
  </div>
);

const DeleteAccountTab = () => (
  <div>
    <SectionTitle>Delete Your Account</SectionTitle>
    <P>If you wish to permanently delete your account and all associated data, please contact us at:</P>
    <div className="mt-4 p-6 bg-[#FF3B30]/10 border border-[#FF3B30]/20">
      <a
        href="mailto:traderresetai@protonmail.com"
        className="text-[#FF3B30] font-bold text-lg hover:underline"
        data-testid="delete-account-email"
      >
        traderresetai@protonmail.com
      </a>
    </div>
    <P>Please include the email address associated with your account in your request. We will process your deletion request within 30 days.</P>
    <P>Once your account is deleted:</P>
    <BulletList items={[
      'All personal data will be permanently removed',
      'Your posts and comments will be deleted',
      'Your journal entries and chat history will be erased',
      'Any active subscriptions will be cancelled',
      'This action cannot be undone',
    ]} />
  </div>
);

const AboutPage = () => {
  const [activeTab, setActiveTab] = useState('about');

  const renderContent = () => {
    switch (activeTab) {
      case 'about': return <AboutTab />;
      case 'terms': return <TermsTab />;
      case 'privacy': return <PrivacyTab />;
      case 'copyright': return <CopyrightTab />;
      case 'community': return <CommunityTab />;
      case 'contact': return <ContactTab />;
      case 'delete': return <DeleteAccountTab />;
      default: return <AboutTab />;
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20 pb-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-heading font-black text-3xl uppercase text-white mb-6" data-testid="about-title">
          About
        </h1>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 border-b border-white/10 pb-4" data-testid="about-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-bold uppercase tracking-wider transition-colors ${
                activeTab === tab.id
                  ? 'bg-[#007AFF] text-white'
                  : 'text-[#8892B0] hover:text-white hover:bg-white/5'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="bg-[#131313] border border-white/5 p-6 sm:p-8" data-testid="about-content">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
